import { useCallback, useEffect, useRef, useState } from "react";
import {
  ColorType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import { api, Unauthorized } from "../lib/api";
import { DrawingLayer, drawingsApi, type Drawing, type DrawingKind } from "../lib/drawings";

type Tool = "cursor" | DrawingKind;

/**
 * The centre workspace: a price chart you can draw on.
 *
 * lightweight-charts is the finance-industry default look (canvas, crosshair, tight
 * candles) and is why this reads as a terminal rather than a dashboard. Drawings are
 * rendered by a series *primitive* (see lib/drawings) so they live in chart space and
 * stay glued to the data through zoom and pan.
 *
 * Interaction: pick a tool, press, drag, release. While a tool is active the chart's
 * own pan/zoom is disabled — otherwise the drag would both draw a line and scroll the
 * chart out from under it.
 */
export function Workspace({ ticker }: { ticker: string | null }) {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const layer = useRef<DrawingLayer | null>(null);
  const dragStart = useRef<{ time: number; price: number } | null>(null);

  const [tool, setTool] = useState<Tool>("cursor");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);

  // Create the chart once. Recreating it per ticker would throw away the user's
  // zoom/pan — and, worse, their drawings mid-session.
  useEffect(() => {
    if (!container.current) return;
    const c = createChart(container.current, {
      layout: {
        background: { type: ColorType.Solid, color: "#0b0d10" },
        textColor: "#7d8794",
        fontFamily: "JetBrains Mono, SF Mono, Menlo, monospace",
        fontSize: 11,
      },
      grid: { vertLines: { color: "#1e2329" }, horzLines: { color: "#1e2329" } },
      rightPriceScale: { borderColor: "#1e2329" },
      timeScale: { borderColor: "#1e2329", rightOffset: 4 },
      crosshair: { mode: 0 },
    });
    chart.current = c;
    const s = c.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderVisible: false,
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });
    series.current = s;
    const l = new DrawingLayer();
    layer.current = l;
    s.attachPrimitive(l);

    const ro = new ResizeObserver(() => {
      const el = container.current;
      if (el) c.applyOptions({ width: el.clientWidth, height: el.clientHeight });
    });
    ro.observe(container.current);
    return () => {
      ro.disconnect();
      c.remove();
      chart.current = null;
      series.current = null;
      layer.current = null;
    };
  }, []);

  // Load bars + this user's saved drawings whenever the ticker changes.
  useEffect(() => {
    if (!ticker || !series.current) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    layer.current?.set([]);
    setCount(0);

    api
      .prices(ticker)
      .then((res) => {
        if (cancelled || !series.current) return;
        series.current.setData(
          res.bars.map((b) => ({
            time: (Date.parse(b.date) / 1000) as UTCTimestamp,
            open: b.open,
            high: b.high,
            low: b.low,
            close: b.close,
          })),
        );
        chart.current?.timeScale().fitContent();
        return drawingsApi.load(ticker);
      })
      .then((saved) => {
        if (cancelled || !saved) return;
        layer.current?.set(saved);
        setCount(saved.length);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Unauthorized ? "session expired" : `no price history for ${ticker}`);
        series.current?.setData([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  // Drawing mode must disable the chart's own drag handlers, or one gesture would
  // both draw and pan.
  useEffect(() => {
    chart.current?.applyOptions({
      handleScroll: tool === "cursor",
      handleScale: tool === "cursor",
    });
  }, [tool]);

  const pointAt = useCallback((clientX: number, clientY: number) => {
    const el = container.current;
    if (!el || !chart.current || !series.current) return null;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const time = chart.current.timeScale().coordinateToTime(x);
    const price = series.current.coordinateToPrice(y);
    if (time === null || price === null) return null;
    return { time: time as number, price };
  }, []);

  const persist = useCallback(
    async (next: Drawing[]) => {
      if (!ticker) return;
      setSaving(true);
      try {
        const saved = await drawingsApi.save(ticker, next);
        layer.current?.set(saved);
        setCount(saved.length);
      } catch {
        setError("could not save drawings");
      } finally {
        setSaving(false);
      }
    },
    [ticker],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (tool === "cursor") return;
    const p = pointAt(e.clientX, e.clientY);
    if (!p) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStart.current = p;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStart.current || tool === "cursor") return;
    const p = pointAt(e.clientX, e.clientY);
    if (!p) return;
    layer.current?.setPreview({ kind: tool, p1: dragStart.current, p2: p });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragStart.current || tool === "cursor") return;
    const p = pointAt(e.clientX, e.clientY) ?? dragStart.current;
    const start = dragStart.current;
    dragStart.current = null;
    layer.current?.setPreview(null);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    // A click without a drag is almost always a misclick, not a zero-length line.
    const isClick = Math.abs(p.time - start.time) < 1 && Math.abs(p.price - start.price) < 1e-9;
    if (isClick && tool !== "horizontal") return;
    void persist([...(layer.current?.all() ?? []), { kind: tool, p1: start, p2: p }]);
    setTool("cursor"); // one line per selection, like every charting tool
  };

  const undo = () => {
    const all = layer.current?.all() ?? [];
    if (all.length) void persist(all.slice(0, -1));
  };

  const clear = () => {
    if (layer.current?.all().length) void persist([]);
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="h-8 shrink-0 flex items-center gap-2 px-3 border-b border-term-edge">
        <span className="font-medium">{ticker ?? "—"}</span>
        {loading && <span className="text-term-dim text-[11px] animate-pulse">loading…</span>}
        {error && <span className="text-term-dim text-[11px]">{error}</span>}

        <div className="flex-1" />

        <ToolButton active={tool === "cursor"} onClick={() => setTool("cursor")} title="Pan / zoom">
          cursor
        </ToolButton>
        <ToolButton
          active={tool === "trendline"}
          onClick={() => setTool("trendline")}
          title="Drag to draw a trendline"
        >
          trendline
        </ToolButton>
        <ToolButton
          active={tool === "horizontal"}
          onClick={() => setTool("horizontal")}
          title="Click to place a price level"
        >
          level
        </ToolButton>
        <span className="w-px h-4 bg-term-edge mx-1" />
        <button onClick={undo} className="text-term-dim hover:text-term-text text-[11px] px-1">
          undo
        </button>
        <button onClick={clear} className="text-term-dim hover:text-term-text text-[11px] px-1">
          clear
        </button>
        <span className="text-term-dim text-[10px] tnum w-16 text-right">
          {saving ? "saving…" : `${count} drawn`}
        </span>
      </div>

      <div
        ref={container}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`flex-1 min-h-0 ${tool === "cursor" ? "" : "cursor-crosshair"}`}
      />
    </div>
  );
}

function ToolButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
        active
          ? "border-term-accent text-term-accent bg-term-accent/10"
          : "border-term-edge text-term-dim hover:text-term-text"
      }`}
    >
      {children}
    </button>
  );
}
